// web/src/features/account/sportsPaySignUp/hooks/useSportsPayCallbackEffect.ts
import {
  isSportsPayCallback,
  readSportsPayCallbackParams,
  readSportsPayCompletionFields,
} from '@sportninja/common/features/account/sportsPaySignUp'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { App } from 'antd'
import { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useNotifySportsPayComplete } from './useNotifySportsPayComplete'

export interface UseSportsPayCallbackEffectArgs {
  /** The account the org belongs to — from the org payload's `account_id`. */
  accountId?: string
  /** Re-reads the organization once completion is recorded. */
  onOnboardingChanged?: () => void
}

/**
 * Records a SportsPay registration completed in a NEW TAB.
 *
 * The embedded path never reaches here: there, the framed callback posts up to
 * `SportsPayUrlButton`, which does the same PUT and closes the frame. This
 * covers the user who took the frame's "Open in New Tab" link instead — SportsPay
 * redirects that tab to the static callback page, which forwards them here with
 * its params intact.
 *
 * `account_id` arrives on the org payload the page is already fetching, so no
 * extra lookup is needed; the effect simply waits for it.
 *
 * Fires AT MOST ONCE per mount (`hasRunRef`) and strips the params afterwards,
 * so neither a re-render nor a refresh can file a duplicate.
 */
export const useSportsPayCallbackEffect = ({
  accountId,
  onOnboardingChanged,
}: UseSportsPayCallbackEffectArgs): void => {
  const { message } = App.useApp()
  const history = useHistory()
  const location = useLocation()
  const notifyComplete = useNotifySportsPayComplete()
  const hasRunRef = useRef(false)

  useEffect(() => {
    if (hasRunRef.current || !isSportsPayCallback(location.search)) {
      return
    }
    // The org fetch is in flight on first render; wait for the account id
    // rather than bailing, so a slow response does not lose the callback.
    if (!accountId) {
      return
    }

    const fields = readSportsPayCompletionFields(
      readSportsPayCallbackParams(location.search)
    )
    hasRunRef.current = true

    const run = async () => {
      try {
        if (fields) {
          await notifyComplete.mutateAsync({ accountId, ...fields })
        }
        message.success('Your SportsPay registration is complete.')
      } catch (err) {
        // Surfaced, but the refetch below still runs: if the backend recorded
        // completion another way, the page updates regardless.
        message.error(getErrorMessage(err))
      } finally {
        onOnboardingChanged?.()
        // Strip the SportsPay params so a refresh cannot re-fire the PUT or
        // re-toast — `hasRunRef` only guards within a single mount.
        history.replace(location.pathname)
      }
    }
    run()
  }, [
    accountId,
    location.search,
    location.pathname,
    history,
    message,
    notifyComplete,
    onOnboardingChanged,
  ])
}
